import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import {
  Accordion,
  AccordionSummary,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useMutation,useQueries } from "@tanstack/react-query";
import { useContext,useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from 'react-toastify';
import { UserContext } from "../../../context/UserContext";
import AttToken from "../../../helpers/attToken";
import { trackEventMatomo } from "../../../helpers/matomo";
import queryClient from "../../../service/query";
import ServiceTypes from "../../../service/ServiceTypes";
import Teams from "../../../service/Teams";
import Users from "../../../service/Users";
import { ServiceType } from "../../../types/managements/types";
import { Priority,Team,TeamsTypeResponse } from "../../../types/teams/types";
import GenericAutoComplete from "../../GenericAutoComplete";
import AssociaConjuntos from "./AssociaConjuntos";

const TeamsService = new Teams();
const usersService = new Users();
const serviceType = new ServiceTypes();

const GridComp = ({ value, onClick, onRemove }: { value: string, onClick: Function, onRemove: Function }) => {
  const { t } = useTranslation('translation');
  return (
    <Grid size={4}>
      <Tooltip title={value}>
        <Card>
          <CardContent>
            <Typography noWrap>{value.length > 20 ? `${value.slice(0, 20)}...` : value}</Typography>
          </CardContent>
          <CardActions>
            <Button color="error" onClick={() => onRemove()}>
              {t('delete')}
            </Button>
            <Button onClick={() => onClick()} size="small">{t('edit')}</Button>
          </CardActions>
        </Card>
      </Tooltip>
    </Grid>);
};

export default function ChangeTeamModal({ setEquipe, equipe, open, setOpen }: { setEquipe: Function, equipe: Team, open: boolean, setOpen: Function }) {
  const [tipoSelecionado, setTipoSelecionado] = useState('');
  const [expanded, setExpanded] = useState<string | boolean>(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [conjuntosAssociados, setConjuntosAssociados] = useState<string[]>([]);
  const [option, setOption] = useState<'tiposAtendimento' | 'notificaSobre' | 'tiposLocaisAssocia'>('tiposLocaisAssocia');
  const { setChanged } = useContext(UserContext);

  const handleChange = (isExpanded: boolean, panel: string) => {
    setExpanded(isExpanded ? panel : false);
  };

  const { t } = useTranslation('translation');

  const results: TeamsTypeResponse = useQueries({
    queries: [
      {
        queryKey: ['GET_USERS'],
        queryFn: async () => {
          try {
            const token = await AttToken();
            if (!token) throw new Error('Token not found');
            if (token) {
              const result = await usersService.getAllUsers({ ...token });
              if (!result) throw new Error('Error fetching users');
              if (result) return result.users;
            }
          } catch (err) {
            throw new Error('Error fetching users');
          }

        },
        staleTime: 5 * 60 * 1000,
        retry: 0,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ['GET_SERVICES'],
        queryFn: async () => {
          try {
            const token = await AttToken();
            if (!token) throw new Error('Token not found');
            if (token) {
              const body = { ...token, empresa: token.userEmpresa };
              const result = await serviceType.getAllByCompany(body);
              if (!result) throw new Error('Error fetching services');
              if (result) return result;
            }
          } catch (err) {
            throw new Error('Error fetching services');
          }
        },
        staleTime: 5 * 60 * 1000,
        retry: 0,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
      }
    ]
  });

  const isLoading = results?.some((result) => result.isLoading);
  const isFetching = results?.some((result) => result.isFetching);

  function editConjuntos(tipo: 'tiposAtendimento' | 'notificaSobre' | 'tiposLocaisAssocia', selecionado: string) {
    setEquipe({ ...equipe, [tipo]: equipe[tipo]?.map((t) => t.tipo === selecionado ? { ...t, cnjs: conjuntosAssociados } : t) });
  }

  function editaPrioridade(selecionado: string, prioridade: Priority) {
    const atendimento = equipe.tiposAtendimento?.find((t) => t.tipo === selecionado);
    if (atendimento) {
      const prioridadeAtual = atendimento.prioridade ?? [];
      const novaPrioridade = prioridadeAtual.includes(prioridade)
        ? prioridadeAtual.filter((p: Priority) => p !== prioridade)
        : [...prioridadeAtual, prioridade];
      setEquipe({
        ...equipe, tiposAtendimento: equipe.tiposAtendimento
          ?.map((t) => t.tipo === selecionado ? { ...t, prioridade: novaPrioridade } : t)
      });
    }
  }

  const { mutate, isPending } = useMutation({
    mutationKey: ['changeTeam'],
    mutationFn: async () => {
      const promise = toast.promise(async () => {
        trackEventMatomo('modal edição de equipe', 'click', 'button', 'salva');
        const token = await AttToken();
        if (!token) throw new Error('Token não encontrado');
        if (token) {
          const team = {
            nome: equipe.nome,
            tiposServico: equipe.tiposServico,
            status: equipe.status,
            users: Array.isArray(equipe.users) ? equipe.users : Object.keys(equipe.users ?? []),
            tiposLocaisAssocia: equipe.tiposLocaisAssocia,
            tiposAtendimento: equipe.tiposAtendimento,
            notificaSobre: equipe.notificaSobre
          };
          const res = await TeamsService.updateTeam(token, equipe.teamID, team);
          if (!res.team) throw new Error('Erro ao atualizar equipe');
          return res;
        }
      }, {
        pending: t('teamManage.register.changingTeam'),
        success: t('teamManage.modal.alter'),
        error: t('teamManage.modal.errorAlt')
      }, { toastId: 'changeTeam' });
      return promise;
    },
    onSuccess: () => {
      trackEventMatomo('gestão de equipes', 'sucesso', 'edição', 'equipe atualizada');
      setOpen();
      queryClient.refetchQueries({ queryKey: ['GET_TEAMS'] });
      setChanged(true);
    },
    onError: () => trackEventMatomo('gestão de equipes', 'erro', 'edição', 'falha ao atualizar equipe'),
  });

  const loading = isLoading || isFetching || isPending;

  const findServiceName = (value: string): string => {
    const serviceName = results?.[1]?.data?.find((el: ServiceType) => el.tipoId === value)?.nome;
    if (serviceName) return serviceName;
    else return `${t('teamManage.serviceNotFound')}`;
  };

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      open={open}
      scroll="paper"
      onClose={() => setOpen()}
    >
      <DialogTitle>{t('teamManage.registered.edit')}</DialogTitle>
      {!openEdit ? <IconButton
        aria-label="close"
        onClick={() => setOpen()}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
        : <Button
          sx={() => ({
            position: 'absolute',
            right: 8,
            top: 8,
          })}
          startIcon={<ArrowBackIcon />}
          className="button"
          onClick={() => {
            trackEventMatomo('modal associação de equipes', 'click', 'button', 'fecha modal');
            setOpenEdit(false);
            setTipoSelecionado('');
            setConjuntosAssociados([]);
          }}
        >
          {t('teamManage.registered.voltar')}
        </Button>}
      <DialogContent dividers={true}>
        {openEdit &&
          <AssociaConjuntos
            tipoServico={tipoSelecionado}
            conjuntosAssociados={conjuntosAssociados}
            setConjuntosAssociados={setConjuntosAssociados}
            nomeServico={findServiceName(tipoSelecionado)}
            editConjuntos={editConjuntos}
            option={option}
            editaPrioridade={editaPrioridade}
            prioridade={equipe.tiposAtendimento?.find((e) => e.tipo === tipoSelecionado)?.prioridade ?? []}
          />}
        {!openEdit
          && <Stack direction='column' alignItems='stretch' justifyContent='flex-start' spacing={2}>
            <TextField
              disabled={loading}
              onClick={() => trackEventMatomo('modal edição de equipe', 'click', 'textfield', 'edita valor')}
              label={t('team')}
              fullWidth
              value={equipe.nome}
              slotProps={{ htmlInput: { maxLength: 80 } }}
              onChange={(e) => setEquipe((prev: Team) => prev ? { ...prev, nome: e.target.value } : prev)}
            />
            {/* Tipo de serviço */}
            <Accordion style={{ padding: 2 }} expanded={expanded === 'panel1'}>
              <AccordionSummary
                disabled={loading}
                expandIcon={<ArrowDownwardIcon
                  onClick={(isExpanded) => {
                    trackEventMatomo('modal edição de equipe', 'click', 'acordeon', 'abre/fecha');
                    if (expanded === 'panel1') {
                      handleChange(!isExpanded, '');
                    } else handleChange(true, 'panel1');
                  }}
                />}>
                <Typography color='textDisabled' fontWeight={600}>{t('Services.servicesManagement.kind')}</Typography>
              </AccordionSummary>
              {expanded === 'panel1'
                && <Stack direction='column' spacing={2} padding={2}>
                  <Grid container rowSpacing={1} columnSpacing={1}>
                    {equipe.tiposServico?.map((ele, i) => (
                      <GridComp key={i} value={findServiceName(ele)}
                        onRemove={() =>
                          setEquipe((prev: Team) => prev
                            ? { ...prev, tiposServico: prev.tiposServico?.filter((item) => item !== ele) }
                            : prev)
                        }
                        onClick={() => {
                          trackEventMatomo('modal edição de equipe', 'click', 'button', 'fecha modal');
                          setTipoSelecionado(ele);
                          setOption('tiposLocaisAssocia');
                          setConjuntosAssociados(equipe.tiposLocaisAssocia ? equipe?.tiposLocaisAssocia?.find((elem) => elem.tipo === ele)?.cnjs ?? [] : []);
                          setOpenEdit(true);
                        }} />
                    ))}
                  </Grid>
                  <GenericAutoComplete
                    label={t('teamManage.register.kindOfService')}
                    options={[...new Set(results[1].data)].map((type) => ({ label: type.nome, value: type.tipoId })) ?? []}
                    selectedValues={equipe.tiposServico?.map((t) => t) || []}
                    onChange={(values) => {
                      const updatedLocais = values.map((v) => ({
                        tipo: v,
                        cnjs: []
                      }));
                      const updated = values.map((v) => ({
                        tipo: v,
                        conjuntosAssociados: null,
                        cnjs: []
                      }));
                      setEquipe((prev: Team) => prev ? { ...prev, tiposServico: updated.map((u) => u.tipo), tiposLocaisAssocia: updatedLocais } : prev);
                    }}
                    loading={loading}
                  />
                </Stack>}
            </Accordion>
            {/* Tipos de atendimento */}
            <Accordion style={{ padding: 2 }} expanded={expanded === 'panel2'}>
              <AccordionSummary
                disabled={loading}
                expandIcon={<ArrowDownwardIcon
                  onClick={(isExpanded) => {
                    trackEventMatomo('modal edição de equipe', 'click', 'acordeon', 'abre/fecha');
                    if (expanded === 'panel2') {
                      handleChange(!isExpanded, '');
                    } else handleChange(true, 'panel2');
                  }}
                />}>
                <Typography color='textDisabled' fontWeight={600}>{t('Services.servicesManagement.AtenderDe').replace(':', '')}</Typography>
              </AccordionSummary>
              {expanded === 'panel2'
                && <Stack direction='column' spacing={2} padding={2}>
                  <Grid container rowSpacing={1} columnSpacing={1}>
                    {equipe.tiposAtendimento?.map((ele, i: number) => (
                      <GridComp key={i} value={findServiceName(ele.tipo)}
                        onRemove={() => setEquipe((prev: Team) => prev
                          ? { ...prev, tiposAtendimento: prev.tiposAtendimento?.filter((item) => item.tipo !== ele.tipo) }
                          : prev)}
                        onClick={() => {
                          trackEventMatomo('modal edição de equipe', 'click', 'button', 'fecha modal');
                          setTipoSelecionado(ele.tipo ?? "");
                          setConjuntosAssociados(equipe.tiposAtendimento.find((elem) => elem.tipo === ele.tipo)?.cnjs ?? []);
                          setOption('tiposAtendimento');
                          setOpenEdit(true);
                        }} />
                    ))}
                  </Grid>
                  <GenericAutoComplete
                    label={t('teamManage.register.kindOfService')}
                    options={[...new Set(results[1].data)].map((type) => ({ label: type.nome, value: type.tipoId })) ?? []}
                    selectedValues={equipe.tiposAtendimento?.map((t) => t.tipo) ?? []}
                    onChange={(values) => {
                      const updated = values.map((v) => ({
                        tipo: v,
                        prioridade: ['baixa' as Priority],
                        cnjs: [],
                      }));
                      setEquipe((prev: Team) => prev ? { ...prev, tiposAtendimento: updated } : prev);
                    }}
                    loading={loading}
                  />
                </Stack>}
            </Accordion>
            {/* Tipos de notificação */}
            <Accordion style={{ padding: 2 }} expanded={expanded === 'panel3'}>
              <AccordionSummary
                disabled={loading}
                expandIcon={<ArrowDownwardIcon
                  onClick={(isExpanded) => {
                    trackEventMatomo('modal edição de equipe', 'click', 'acordeon', 'abre/fecha');
                    if (expanded === 'panel3') {
                      handleChange(!isExpanded, '');
                    } else handleChange(true, 'panel3');
                  }}
                />}>
                <Typography color='textDisabled' fontWeight={600}>{t('Services.servicesManagement.serNotificado')}</Typography>
              </AccordionSummary>
              {expanded === 'panel3'
                && <Stack direction='column' spacing={2} padding={2}>
                  <Grid container rowSpacing={1} columnSpacing={1}>
                    {equipe.notificaSobre?.map((ele, i: number) => (
                      <GridComp key={i} value={findServiceName(ele.tipo)}
                        onRemove={() => setEquipe((prev: Team) => prev
                          ? { ...prev, notificaSobre: prev.notificaSobre?.filter((item) => item.tipo !== ele.tipo) }
                          : prev)}
                        onClick={() => {
                          trackEventMatomo('modal edição de equipe', 'click', 'button', 'fecha modal');
                          setTipoSelecionado(ele.tipo ?? "");
                          setConjuntosAssociados(equipe.notificaSobre.find((elem) => elem.tipo === ele.tipo)?.cnjs ?? []);
                          setOption('notificaSobre');
                          setOpenEdit(true);
                        }} />
                    ))}
                  </Grid>
                  <GenericAutoComplete
                    label={t('teamManage.register.kindOfService')}
                    options={[...new Set(results[1].data)].map((type) => ({ label: type.nome, value: type.tipoId })) ?? []}
                    selectedValues={equipe.notificaSobre?.map((t) => t.tipo) ?? []}
                    onChange={(values) => {
                      const updated = values.map((v) => ({
                        tipo: v,
                        cnjs: [],
                      }));
                      setEquipe((prev: Team) => prev ? { ...prev, notificaSobre: updated } : prev);
                    }}
                    loading={loading}
                  />
                </Stack>}
            </Accordion>
            <GenericAutoComplete
              label={t('Services.servicesManagement.members')}
              options={[...new Set(results[0].data)].map((user) => ({ label: user.nome, value: user.id })) ?? []}
              selectedValues={Array.isArray(equipe.users) ? equipe.users : Object.keys(equipe.users ?? [])}
              onChange={(values) => setEquipe((prev: Team) => prev ? { ...prev, users: values } : prev)}
              loading={loading}
            />
            <FormControl fullWidth>
              <InputLabel>{t('status')}</InputLabel>
              <Select
                disabled={loading}
                label={t('status')}
                onClick={() => trackEventMatomo('modal edição de equipe', 'click', 'select', 'seleciona valor')}
                onChange={(e) => setEquipe((prev: Team) => prev ? { ...prev, status: e.target.value === 'ativo' ? true : false } : prev)}
                defaultValue={equipe.status ? 'ativo' : 'inativo'}
              >
                <MenuItem value={'ativo'}>{t('active')}</MenuItem>
                <MenuItem value={'inativo'}>{t('inactive')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>}
      </DialogContent>
      {!openEdit && <DialogActions>
        <Button
          onClick={() => mutate()}
          disabled={loading}
        >
          {t('save')}
        </Button>
      </DialogActions>}
    </Dialog>
  );
}
